import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import {
  Card,
  CardNumber,
  Dots,
  Icon,
  Row,
} from "Payments/PaymentPage/ModifyOrCancelScreen/ModifyOrCancelScreen";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { Subtitle, Title } from "Payments/PaymentPage/Shared/style.js";
import { useGetSelectedPaymentMethodDetails } from "Payments/PaymentPage/Shared/useGetSelectedPaymentMethodDetails";
import { getCardTypePretty } from "Payments/Shared/getCardTypePretty";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import qs from "qs";

import FnrIcon from "UI/FnrIcon";
import FnrPaymentMethodIcon from "UI/FnrPaymentMethodIcon";
import { EditorTypography } from "UI/FnrTextEditor/EditorTypography";

import { formatAmountInCentsForDisplay } from "Utilities/Number/formatAmountInCentsForDisplay";
import { addHttpToUrl } from "Utilities/addHttpToUrl";
import capitalize from "Utilities/capitalize";

import { useHasPaymentCTAs } from "../API/queryHooks";
import CTAs from "../CTAs";
import { ScreenWrapper, sidePadding } from "../Shared/ScreenWrapper";
import { useGetPaymentTypeVerbiage } from "../Shared/useGetPaymentTypeVerbiage";

import Confetti from "./Confetti";
import ReceiptButton from "./ReceiptButton";
import { getAmountToDisplay } from "./getAmountToDisplay";

export default function SuccessScreen({ showing }) {
  const { state } = usePaymentPage();
  const { noun } = useGetPaymentTypeVerbiage();
  const { user, memo, paymentParent, addOnAmounts } = state;
  const {
    receiptSentTo: receiptSentToSubmit,
    recurringSetUp: recurringPayment,
  } = state.submitPaymentResponse;
  const { receiptSentTo: receiptSentToPolling } = state.pollingPaymentResponse;
  const { hideConfetti, confirmationMessage } = state.fund.paymentPageSettings;
  const websiteSafe = addHttpToUrl(state.network?.website);
  const { cardType, nickname, last4 } = useGetSelectedPaymentMethodDetails();
  const cardTypePretty = getCardTypePretty(cardType);

  const { hasPaymentCTAs } = useHasPaymentCTAs(state.fund.ID, showing);
  const location = useLocation();

  const { signupListID } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const signupName = window.data?.paymentPageData?.signupName;

  const getIntervalNoun = (interval) => {
    switch (interval.toLowerCase()) {
      case "weekly":
        return "week";
      case "monthly":
        return "month";
      case "quarterly":
        return "quarter";
      case "annually":
        return "year";
      default:
        return;
    }
  };

  // Note: text message receipts are not built yet, so we don't use phoneNumber as a fallback.
  const receiptSentTo =
    receiptSentToSubmit || receiptSentToPolling || user?.email;

  const childVariants = {
    hidden: {
      opacity: 0,
      y: 100,
    },
    show: { opacity: 1, y: 0 },
  };

  const paymentAlreadyCompleted = state.modifyPayment?.paymentAlreadyCompleted;
  const receiptMessage = () => {
    if (receiptSentTo) {
      return `${
        paymentAlreadyCompleted ? `We've sent` : `We'll send`
      } a receipt to ${receiptSentTo}.`;
    }
    return paymentAlreadyCompleted
      ? "We've sent you a receipt."
      : `We'll send you a receipt once your ${noun} has processed.`;
  };

  const hasAddOnGifts =
    (state?.addOnAmounts || []).filter((a) => a?.isChecked).length > 0;
  const amountToDisplay = getAmountToDisplay(state);
  const paymentDestinationNameText = hasAddOnGifts
    ? "Total"
    : signupListID && signupName
    ? signupName
    : state.fund.description;

  const successCallbackHasBeenRunRef = useRef(false);
  useEffect(() => {
    // Example of use: manages changes at the payment drawer for signup payments on success
    // Example 2: navigates to the closing screen of the family registration flow
    if (
      showing &&
      paymentParent?.successCallback &&
      !successCallbackHasBeenRunRef.current
    ) {
      let res = state.submitPaymentResponse;
      paymentParent.successCallback(res);
      successCallbackHasBeenRunRef.current = true;
    }
  }, [showing, paymentParent, state]);

  return (
    <Wrapper>
      <Confetti
        hide={
          hideConfetti ||
          !showing ||
          paymentAlreadyCompleted ||
          paymentParent?.hidePaymentsConfetti
        }
      />
      <Inside
        initial="hidden"
        animate={showing && "show"}
        variants={{
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: { delay: 0.1, staggerChildren: 0.15 },
          },
        }}
        data-cy="fund-it-confetti"
      >
        <TitleSection variants={childVariants}>
          <Title data-cy="fund-it-success-message">
            {recurringPayment
              ? `Your ${noun} has been scheduled.`
              : paymentAlreadyCompleted
              ? `This ${noun} has already processed.`
              : paymentParent?.successScreen?.title
              ? paymentParent.successScreen.title
              : `Thank you for your ${noun}!`}
          </Title>

          {paymentParent?.successScreen?.subtitle ? (
            <Subtitle>{paymentParent?.successScreen?.subtitle}</Subtitle>
          ) : null}

          <Subtitle>Processing may take up to 24 hours.</Subtitle>

          {paymentAlreadyCompleted && (
            <TimedOut>
              {`It's too late to cancel or modify this payment.  If you'd like to request a refund, please contact `}{" "}
              <a href={websiteSafe} target="_blank" rel="noreferrer">
                {state.network.name}
              </a>
              {` or email `}
              <a
                href={`mailto:${state.network.contactEmail}?subject=Help with a payment&body=[[Please type your message here]]%0D%0A%0D%0A %0D%0A**Notes for Flocknote Support**%0D%0ANetwork: ${state.network.name}%0D%0ANetwork URL: https://${state.network.shortname}.flocknote.com%0D%0AProfile: https://${state.network.shortname}.flocknote.com/dashboard/user/${state.user.ID}`}
              >
                {state.network.contactEmail}
              </a>
              {`.`}
            </TimedOut>
          )}

          {recurringPayment && (
            <RecurringPaymentMessage data-cy="fund-it-success-message-detail">{`We will automatically charge your ${capitalize(
              cardType
            )} $${state.amount} every ${getIntervalNoun(
              state.recurringPayment.frequency
            )} beginning ${DateTime.fromISO(
              state.recurringPayment.startDate
            ).toLocaleString(DateTime.DATE_MED)}${
              state.recurringPayment.endDate
                ? ` and going through ${DateTime.fromISO(
                    state.recurringPayment.endDate
                  ).toLocaleString(DateTime.DATE_MED)}`
                : ""
            }.`}</RecurringPaymentMessage>
          )}
        </TitleSection>

        <AmountSection variants={childVariants}>
          {hasAddOnGifts && (
            <AddOnGiftAmounts
              addOnAmounts={addOnAmounts}
              donationAmount={state.amount}
              fundName={state?.fund?.description}
            />
          )}
          <DonationInfo>
            <PaymentDestinationName data-cy="fund-it-success-fund">
              {paymentDestinationNameText}
            </PaymentDestinationName>
            <Amount
              textSize={hasAddOnGifts ? "48" : "58"}
              data-cy="fund-it-success-amount"
            >
              ${amountToDisplay}
            </Amount>
          </DonationInfo>
          {memo && (
            <Memo hasConfirmationMessage={confirmationMessage?.length > 0}>
              <strong>Memo:</strong> <span>{memo}</span>
            </Memo>
          )}
          {confirmationMessage && (
            <ConfirmationMessage
              dangerouslySetInnerHTML={{
                __html: confirmationMessage,
              }}
            />
          )}
        </AmountSection>

        <PaymentSection $hasCTAs={hasPaymentCTAs}>
          {paymentAlreadyCompleted ? (
            <Row>
              <Icon>
                <FnrPaymentMethodIcon cardType={cardType} />
              </Icon>
              <Card>
                {nickname || cardTypePretty}
                <CardNumber>
                  <Dots>••••</Dots>
                  {last4}
                </CardNumber>
              </Card>
            </Row>
          ) : null}

          <CTAs fundID={state.fund.ID} showing={showing} />

          <ReceiptConfirmation
            variants={childVariants}
            data-cy="fund-it-success-receipt-confirmation"
          >
            <IconHolder>
              <FnrIcon fas fa-receipt />
            </IconHolder>
            {receiptMessage()}
          </ReceiptConfirmation>

          <motion.div variants={childVariants}>
            {/* RECIEPT BUTTON */}
            <ReceiptButton linkView={hasPaymentCTAs} />
          </motion.div>
        </PaymentSection>
      </Inside>
    </Wrapper>
  );
}

const Wrapper = styled(ScreenWrapper)`
  ${sidePadding}
`;
const Inside = styled(motion.div)`
  padding: 40px 0;
`;

const TimedOut = styled.div`
  padding-top: 14px;
`;

const RecurringPaymentMessage = styled(TimedOut)`
  color: #383838;
`;

const PaymentSection = styled.div`
  padding-top: ${(props) => (props.$hasCTAs ? "0px" : "20px")};
  border-top: ${(props) => (props.$hasCTAs ? "none" : "1px solid #98a4a5")};
`;

const AmountSection = styled(motion.div)`
  padding: 34px 0 40px 0;
  margin-top: 40px;
  border-top: 1px solid #98a4a5;
`;

const DonationInfo = styled.div`
  font-family: Inconsolata;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
`;

const PaymentDestinationName = styled.div`
  color: #383838;
  font-weight: 600;
  font-size: 20px;
`;
const Amount = styled.div`
  color: var(--fundColor);
  font-weight: 600;
  //font-size: 58px;
  flex-shrink: 0;
  font-size: ${(p) => p.textSize || "58"}px;
`;

const Memo = styled.div`
  font-family: Inconsolata;
  color: #383838;
  margin-top: 10px;
  font-size: 14px;
  padding-bottom: ${(props) => (props.hasConfirmationMessage ? "40px" : 0)};
  margin-bottom: ${(props) => (props.hasConfirmationMessage ? "20px" : 0)};
  border-bottom: ${(props) =>
    props.hasConfirmationMessage ? "1px solid #98a4a5" : "none"};

  > span {
    font-weight: 400;
  }
`;

const ConfirmationMessage = styled(EditorTypography)`
  color: #383838;
  font-size: 15px;
  font-weight: 400;
  padding-top: 18px;

  > p {
    margin-bottom: 2px;
  }
`;

const TitleSection = styled(motion.div)``;

const ReceiptConfirmation = styled(motion.div)`
  padding-top: 24px;
  font-weight: 400;
  font-size: 15px;
  color: #383838;
  display: flex;
  align-items: center;
  gap: 14px;
`;
const IconHolder = styled.div`
  color: var(--fundColor);
  font-size: 20px;
`;

const Close = styled(motion.div)`
  padding-top: 36px;
  color: var(--fundColor);
  text-align: center;
  font-size: 13px;
  margin-bottom: -10px;
`;

function AddOnGiftAmounts({ donationAmount, addOnAmounts, fundName }) {
  const paymentPageData = window?.data?.paymentPageData;
  const addOnGifts = paymentPageData?.addOnGifts || [];
  const filtered = addOnAmounts.filter((a) => a.isChecked);
  const gifts = filtered.map((f) => {
    const gift = addOnGifts.find((g) => g.addOnID === f.addOnID);
    return {
      ...gift,
      ...f,
    };
  });

  return (
    <AddOnWrapper>
      <AddOnGift>
        <AddOnTitle>
          <span>{fundName}</span>
        </AddOnTitle>
        <AddOnAmount>
          $
          {formatAmountInCentsForDisplay(
            parseFloat(donationAmount) * 100,
            true
          )}
        </AddOnAmount>
      </AddOnGift>
      {gifts.map((a) => (
        <AddOnGift key={a.addOnID}>
          <AddOnTitle>
            <span>{a.title}</span>
          </AddOnTitle>
          <AddOnAmount>
            ${formatAmountInCentsForDisplay(parseFloat(a.amount) * 100, true)}
          </AddOnAmount>
        </AddOnGift>
      ))}
    </AddOnWrapper>
  );
}

const AddOnWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto;
  gap: 2px;
`;

const AddOnGift = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: span 2;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  font-family: Inconsolata;
  color: #383838;
  font-weight: 400;

  @supports not (grid-template-rows: subgrid) {
    grid-template-columns: 1fr auto;
  }
`;

const AddOnTitle = styled.div`
  flex-grow: 1;

  span {
    word-break: break-word;
  }
`;

const AddOnAmount = styled.div``;
